import { createSelector } from '@reduxjs/toolkit';
import { appInsights } from '@/telemetry/client';
import urlsService from '@/services/urls-service';
import { baseApi } from './baseApi';
import { transformGetCartResponse } from './transformCartApi';
import type * as Types from './cartApiTypes';
import type { AppRootState, AppDispatch } from '@/configureStore';

export const CACHE_TAG = 'CART';

export const cartApi = baseApi.enhanceEndpoints({ addTagTypes: [CACHE_TAG] }).injectEndpoints({
  endpoints: (builder) => ({
    addToCart: builder.mutation<any, Types.IAddToCartRequest>({
      query: ({ items }) => {
        const requestBody = {
          lines: items.map((item) => ({
            variantCode: item.variantCode,
            // Strip the variantcode + semicolon
            unitOfMeasureCode: item.unitOfMeasureCode.replace(`${item.variantCode}:`, ''),
            quantity: item.quantity
          }))
        };
        return {
          url: urlsService.getUrl('/cart/additems'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    addSingleItemToCart: builder.mutation<any, Types.IAddSingleItemToCartRequest>({
      query: ({ variantCode, unitOfMeasureCode, quantity }) => {
        const requestBody = {
          variantCode,
          unitOfMeasureCode,
          quantity
        };
        return {
          url: urlsService.getUrl('/cart/additem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    getCart: builder.query<Types.IGetCartResponse, void>({
      query: () => urlsService.getUrl('/cart/current'),
      transformResponse: transformGetCartResponse,
      keepUnusedDataFor: 99999, // Do not remove cache after fixed timeout
      providesTags: [CACHE_TAG]
    }),
    deleteCartItem: builder.mutation<Types.IGetCartResponse, Types.IDeleteCartItemRequest>({
      query: ({ variantCode }) => {
        const requestBody = {
          variantCode: variantCode
        };
        return {
          url: urlsService.getUrl('/cart/deleteItem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    updateCartItem: builder.mutation<Types.IGetCartResponse, Types.IUpdateCartItemRequest>({
      query: ({ variantCode, unitOfMeasureCode, quantity, onAccount, reference }) => {
        const requestBody = {
          variantCode,
          unitOfMeasureCode,
          quantity,
          onAccount,
          reference
        };
        return {
          url: urlsService.getUrl('/cart/updateItem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    replaceCartItem: builder.mutation<Types.IGetCartResponse, Types.ICartReplaceProductRequest>({
      query: (data) => {
        return {
          url: urlsService.getUrl('/cart/replaceItem'),
          method: 'POST',
          body: data
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    updateShippingMethod: builder.mutation({
      query: (methodCode) => {
        return {
          url: urlsService.getUrl(`/cart/updateshippingmethod/${methodCode}`),
          method: 'POST'
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Invalidate the cart cache to trigger a refetch with transformResponse
          dispatch(cartApi.util.invalidateTags([CACHE_TAG]));
          dispatch(cartApi.endpoints.getCart.initiate());
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    registerAnonymous: builder.mutation<Types.IGetCartResponse, Types.IRegisterAnonymousRequest>({
      query: (data) => ({
        url: urlsService.getUrl('cart/registeranonymous'),
        method: 'POST',
        body: data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Invalidate the cart cache to trigger a refetch with transformResponse
          dispatch(cartApi.util.invalidateTags([CACHE_TAG]));
          dispatch(cartApi.endpoints.getCart.initiate());
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    })
  }),
  overrideExisting: true
});

export const {
  useGetCartQuery,
  useAddToCartMutation,
  useAddSingleItemToCartMutation,
  useUpdateShippingMethodMutation,
  useRegisterAnonymousMutation,
  useReplaceCartItemMutation,
  useDeleteCartItemMutation,
  useUpdateCartItemMutation,
  useLazyGetCartQuery
} = cartApi;

export const cartSelector = cartApi.endpoints.getCart.select();

export function selectCart(state: AppRootState) {
  const cart = cartSelector(state ?? []);
  return cart;
}

export const selectCartTotal = (state: AppRootState) => selectCart(state)?.data?.totalPieces;
export const selectLines = (state: AppRootState) => cartSelector(state).data?.lines;
export const selectAddress = (state: AppRootState) => cartSelector(state).data?.address;
export const selectCosts = (state: AppRootState) => cartSelector(state).data?.costs;
export const selectShipmentMethodCode = (state: AppRootState) => cartSelector(state).data?.shipmentMethodCode;

/* eslint-disable prettier/prettier */
export const searchCartItems = createSelector(
  [selectLines, (_, searchTerm) => searchTerm],
  (lines, searchTerm) => {
    if (!searchTerm) {
      return [];
    }
    const matches = lines?.filter(line => {
      const matchCode = line.variantCode.includes(searchTerm.toLowerCase());
      const matchesTitle = line.variant.title.toLowerCase().includes(searchTerm.toLowerCase());
      return matchCode || matchesTitle;
    });

    return matches?.length ? matches : [];
  }
);
/* eslint-enable prettier/prettier */

// Reset shipping costs in query cache. The API does not accept empty fiels
// so we cannot create a new empty address
export const resetShippingCosts = (dispatch: AppDispatch) => {
  dispatch(
    cartApi.util.updateQueryData('getCart', undefined, (draft) => {
      draft.costs.shipment = undefined;
      // can't show costs if they are unknown even if they are in the data.
      draft.costs.grandTotal = undefined;
    })
  );
};
